import { SubmitApplicationDTO } from 'api/application/postApplication'
import { Disclosure } from 'api/lender/getLenderDisclosures'

import { OTHER_INCOME_SOURCES } from '../../form/fields/employmentFields'
import { FormValues } from '../FormBuilder'
import {
    applicantFieldNames,
    coapplicantFieldNames,
} from './sharedInstructions'

export function convertIncomeSourceIdsToString(
    selectedIds: string[] | string | undefined,
): string {
    if (
        !selectedIds ||
        (Array.isArray(selectedIds) && selectedIds.length === 0)
    ) {
        return ''
    }

    const ids = Array.isArray(selectedIds) ? selectedIds : [selectedIds]

    return ids
        .map((id) => {
            const source = OTHER_INCOME_SOURCES.find(
                (incomeSource) => incomeSource.id === id,
            )

            return source ? source.label : ''
        })
        .filter((label) => label !== '')
        .join(', ')
}

export function convertIncomeSourceStringToIds(
    incomeSourceString: string | undefined,
): string[] {
    if (!incomeSourceString || incomeSourceString.trim() === '') {
        return []
    }

    return incomeSourceString
        .split(',')
        .map((label) => label.trim())
        .filter((label) => label !== '')
        .map((label) => {
            const source = OTHER_INCOME_SOURCES.find(
                (incomeSource) => incomeSource.label === label,
            )

            return source ? source.id : ''
        })
        .filter((id) => id !== '')
}

export function buildOtherIncomeFields(
    values: FormValues,
    fieldNames: typeof applicantFieldNames | typeof coapplicantFieldNames,
    calculateMonthlyGross: (amount: string, interval: string) => string,
    prefix: '' | 'coapplicant_' = '',
): Record<string, string | null> {
    if (values[fieldNames.hasOtherIncome]) {
        return {
            [`${prefix}other_income_source`]: convertIncomeSourceIdsToString(
                values[fieldNames.otherIncomeSource],
            ),
            [`${prefix}other_monthly_gross_income`]: calculateMonthlyGross(
                values[fieldNames.otherIncomeGrossAmount],
                values[fieldNames.otherIncomeGrossInterval],
            ),
            [`${prefix}other_income_gross_amount`]:
                values[fieldNames.otherIncomeGrossAmount],
            [`${prefix}other_income_gross_interval`]:
                values[fieldNames.otherIncomeGrossInterval],
        }
    }

    return {
        [`${prefix}other_income_source`]: '',
        [`${prefix}other_monthly_gross_income`]: null,
        [`${prefix}other_income_gross_amount`]: null,
        [`${prefix}other_income_gross_interval`]: '',
    }
}

export function buildResidentialData(
    fieldNames: typeof applicantFieldNames | typeof coapplicantFieldNames,
    values: FormValues,
    prefix: '' | 'coapplicant_' = '',
): Record<string, string> {
    return {
        [`${prefix}primary_address`]: values[fieldNames.street],
        [`${prefix}primary_apt`]: values[fieldNames.apartmentSuite],
        [`${prefix}primary_zip`]: values[fieldNames.zip],
        [`${prefix}primary_state`]: values[fieldNames.state],
        [`${prefix}primary_city`]: values[fieldNames.city],
    }
}

export function buildApplicantData(
    fieldNames: typeof applicantFieldNames | typeof coapplicantFieldNames,
    values: FormValues,
    calculateMonthlyGross: (amount: string, interval: string) => string,
    prefix: '' | 'coapplicant_' = '',
): Record<string, string | undefined> {
    return {
        [`${prefix}first_name`]: values[fieldNames.firstName].toUpperCase(),
        [`${prefix}middle_initial`]:
            values[fieldNames.middleInitial]?.toUpperCase(),
        [`${prefix}last_name`]: values[fieldNames.lastName].toUpperCase(),
        [`${prefix}suffix`]: values[fieldNames.suffix],
        [`${prefix}citizenship_status`]: values[fieldNames.citizenshipStatus],
        [`${prefix}social_security_number`]: values[fieldNames.ssn],
        [`${prefix}dob`]: values[fieldNames.dateOfBirth].replaceAll(/\s/g, ''),
        [`${prefix}residential_status`]: values[fieldNames.residenceStatus],
        [`${prefix}home_phone`]: values[fieldNames.homePhone],
        [`${prefix}mobile_phone`]: values[fieldNames.mobilePhone],
        [`${prefix}work_phone`]:
            values[fieldNames.workPhone] || values[fieldNames.mobilePhone],
        [`${prefix}residential_years`]: values[fieldNames.residentialYears],
        [`${prefix}residential_months`]: values[fieldNames.residentialMonths],
        [`${prefix}monthly_housing_payment`]:
            values[fieldNames.monthlyHousingPayment],
        [`${prefix}job_title`]:
            values[fieldNames.jobTitle] || values[fieldNames.employmentStatus],
        [`${prefix}employment_status`]: values[fieldNames.employmentStatus],
        [`${prefix}employer_name`]: values[fieldNames.employerName],
        [`${prefix}employer_years`]: values[fieldNames.employerYears],
        [`${prefix}employer_months`]: values[fieldNames.employerMonths],
        [`${prefix}employer_monthly_gross`]: calculateMonthlyGross(
            values[fieldNames.employerGrossAmount],
            values[fieldNames.employerGrossInterval],
        ),
        [`${prefix}employer_gross_amount`]:
            values[fieldNames.employerGrossAmount],
        [`${prefix}employer_gross_interval`]:
            values[fieldNames.employerGrossInterval],
        [`${prefix}email_address`]: values[fieldNames.email],
    }
}

export function addDisclosuresToDTO(
    dto: SubmitApplicationDTO,
    values: FormValues,
    disclosures: Disclosure[] | undefined,
): SubmitApplicationDTO {
    if (!disclosures) {
        return dto
    }

    const matchedDisclosures = disclosures.filter(
        (item) =>
            item.name === values.consent.disclosure ||
            item.name === values.customerVerification.disclosure,
    )

    if (matchedDisclosures.length === 0) {
        return dto
    }

    return {
        ...dto,
        disclosures: matchedDisclosures.map((matchedDisclosure) => ({
            disclosure_id: matchedDisclosure.id,
            checked:
                matchedDisclosure.name === values.consent.disclosure
                    ? values.consent.checked
                    : values.customerVerification.checked,
        })),
    }
}
